import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useIsMobile } from "@/hooks/use-mobile";
import { TooltipProvider } from "@/components/ui/tooltip";
import { MarginAlertBadge } from "./MarginAlertBadge";
import { ChevronDown, ChevronUp } from "lucide-react";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

interface Props {
  contractValue: number;
  estimatedCost: number;
  committedCost: number;
  actualCost: number;
  billedToDate: number;
  collectedToDate: number;
  estimatedMargin: number;
  margin: number;
  actualCostMargin: number;
}

export const ProjectKPICards = ({
  contractValue, estimatedCost, committedCost, actualCost,
  billedToDate, collectedToDate, estimatedMargin, margin, actualCostMargin
}: Props) => {
  const { can } = usePermissions();
  const showFinancials = can("view_financials");
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);

  const marginColor = (v: number) => v >= 20 ? "text-green-600" : v >= 10 ? "text-yellow-600" : "text-red-600";
  const marginDot = (v: number) => v >= 20 ? "bg-green-500" : v >= 10 ? "bg-yellow-500" : "bg-red-500";

  const cards = [
    { label: "Contract Value", value: showFinancials ? fmt(contractValue) : "—", dot: "bg-primary" },
    { label: "Estimated Cost", value: showFinancials ? fmt(estimatedCost) : "—", dot: "bg-indigo-500" },
    { label: "Committed Cost", value: showFinancials ? fmt(committedCost) : "—", dot: "bg-blue-500" },
    { label: "Actual Cost", value: showFinancials ? fmt(actualCost) : "—", dot: "bg-orange-500" },
    { label: "Billed to Date", value: showFinancials ? fmt(billedToDate) : "—", dot: "bg-emerald-500" },
    { label: "Collected", value: showFinancials ? fmt(collectedToDate) : "—", dot: "bg-violet-500" },
    { label: "Est. Margin", value: showFinancials ? `${estimatedMargin.toFixed(1)}%` : "—", dot: marginDot(estimatedMargin), valueClass: showFinancials ? marginColor(estimatedMargin) : "text-muted-foreground" },
    { label: "Committed Margin", value: showFinancials ? `${margin.toFixed(1)}%` : "—", dot: marginDot(margin), valueClass: showFinancials ? marginColor(margin) : "text-muted-foreground", alert: showFinancials ? { est: estimatedMargin, cur: margin, lbl: "committed" as const } : undefined },
    { label: "Actual Margin", value: showFinancials ? `${actualCostMargin.toFixed(1)}%` : "—", dot: marginDot(actualCostMargin), valueClass: showFinancials ? marginColor(actualCostMargin) : "text-muted-foreground", alert: showFinancials ? { est: estimatedMargin, cur: actualCostMargin, lbl: "actual" as const } : undefined },
  ];

  // On mobile, show only top 3 (Contract Value, Actual Cost, Actual Margin) unless expanded
  const MOBILE_PRIORITY = [0, 3, 8]; // indices
  const visibleCards = isMobile && !expanded
    ? MOBILE_PRIORITY.map((i) => cards[i])
    : cards;

  return (
    <TooltipProvider>
      <div className="grid gap-4 grid-cols-3 lg:grid-cols-3 xl:grid-cols-9">
        {visibleCards.map((c) => (
          <Card key={c.label} className="border bg-card">
            <CardContent className="pt-4 pb-3 px-4 md:pt-5 md:pb-4 md:px-5">
              <div className="flex items-center gap-2 mb-1">
                <span className={cn("h-2 w-2 rounded-full shrink-0", c.dot)} />
                <span className="text-[10px] md:text-xs font-medium text-muted-foreground uppercase tracking-wide truncate">{c.label}</span>
              </div>
              <p className={cn("text-lg md:text-2xl font-bold tracking-tight", c.valueClass)}>{c.value}</p>
              {c.alert && (
                <div className="mt-1">
                  <MarginAlertBadge
                    estimatedMarginPct={c.alert.est}
                    currentMarginPct={c.alert.cur}
                    label={c.alert.lbl}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      {isMobile && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-1 text-xs text-muted-foreground"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <><ChevronUp className="h-3 w-3 mr-1" /> Show less</>
          ) : (
            <><ChevronDown className="h-3 w-3 mr-1" /> Show all {cards.length} metrics</>
          )}
        </Button>
      )}
    </TooltipProvider>
  );
};
